import { memo, useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import { getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { securityKeysFromRefs, useOrderBooksTick, useSnapshotsTick } from "../../hooks/useSecurityMarketTicks";
import { toLocaleStringIn } from "../../util/general";
import { useStyles } from "./styles";
import { selectOrderType, selectTradeOrders } from "../../selectors/strategyBuilder";
import { Broker_OrderType } from "../../proto/generated/order";
import { AnalyzeGroupedPosition } from "../../reducer/positions.reducer";

export type AnalyzePositionsSummaryProfitCellProps = {
    analyzeGroupedPosition: AnalyzeGroupedPosition
    positions: { security: { exchange: number; code: string } }[]
    handleProfitChange?: (realisedProfit: number, unrealisedProfit: number) => void
}

const AnalyzePositionsSummaryProfitCell: React.FC<AnalyzePositionsSummaryProfitCellProps> = ({
    analyzeGroupedPosition,
    positions,
    handleProfitChange,
}) => {
    const { classes, cx } = useStyles();
    const tradeOrders = useSelector(selectTradeOrders);
    const orderType = useSelector(selectOrderType);

    const marketTickKeys = useMemo(() => {
        const refs: { exchange: number; code: string }[] = positions.map((p) => p.security);
        tradeOrders.forEach((o) => refs.push(o.security));
        return securityKeysFromRefs(refs);
    }, [positions, tradeOrders]);

    const snapshots = useSnapshotsTick(marketTickKeys) ?? {};
    const orderBooks = useOrderBooksTick(marketTickKeys) ?? {};

    const profitPair = useMemo(() => {
        return analyzeGroupedPosition?.children.map((position) => {
            if (position.children?.length > 1) {
                return [0, 0];
            }

            const securityKey = getSecurityKey(
                position.security.exchange,
                position.security.code
            );

            const snapshot = snapshots[securityKey];
            const orderBook = orderBooks[securityKey];

            const { squareOffOrderId } = position;
            const squareOffOrder = tradeOrders?.find(
                (t) => t.clientOrderId === squareOffOrderId
            );

            const squareOffPrice =
                squareOffOrder && squareOffOrder.selected
                    ? squareOffOrder.price && squareOffOrder.price !== 0 && orderType === Broker_OrderType.LIMIT_ORDER
                        ? squareOffOrder.price
                        : position.originalPosition.closePosition > 0
                          ? (orderBook?.bids[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                          : (orderBook?.asks[0]?.price ?? snapshot?.lastTradedPrice ?? 0)
                    : 0;

            const {
                security,
                avgPrice,
                newQuantity,
                squaredOffQuantity,
            } = position;

            const priceMultiplier = security.priceMultiplier ?? 100;

            let realisedProfit = position.realizedProfit || 0;

            if (squaredOffQuantity !== 0 && squareOffPrice) {
                const originalPosition = position.originalPosition;
                if (originalPosition) {
                    const direction = originalPosition.closePosition > 0 ? 1 : -1;
                    const originalAvgPrice =
                        originalPosition.closePositionValue /
                        originalPosition.closePosition;

                    realisedProfit +=
                        direction *
                        squaredOffQuantity *
                        (squareOffPrice - originalAvgPrice);
                }
            }

            realisedProfit = realisedProfit / priceMultiplier;

            let unrealisedProfit = 0;

            if (newQuantity !== 0 && snapshot) {
                const isIncoming = (position.addedQuantity ?? 0) !== 0;
                let entryPrice = avgPrice;

                if (isIncoming) {
                    entryPrice = snapshot.lastTradedPrice;
                }

                unrealisedProfit =
                    ((snapshot.lastTradedPrice - entryPrice) * newQuantity) /
                    priceMultiplier;
            }

            return [realisedProfit, unrealisedProfit];
        });
    }, [analyzeGroupedPosition, snapshots, tradeOrders, orderType, orderBooks]);

    const realisedProfit = useMemo(
        () => (profitPair || []).reduce((sum, i) => sum + i[0], 0),
        [profitPair]
    );
    const unrealisedProfit = useMemo(
        () => (profitPair || []).reduce((sum, i) => sum + i[1], 0),
        [profitPair]
    );

    useEffect(() => {
        handleProfitChange?.(realisedProfit, unrealisedProfit);
    }, [realisedProfit, unrealisedProfit, handleProfitChange]);

    return (
        <Typography
            variant="body2"
            className={cx(
                unrealisedProfit > 0 ? classes.profitColor : undefined,
                unrealisedProfit < 0 ? classes.lossColor : undefined
            )}
            style={{ textAlign: "right" }}
        >
            {toLocaleStringIn(unrealisedProfit ?? 0)}
        </Typography>
    );
};

export default memo(AnalyzePositionsSummaryProfitCell);
